import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Badge, Table } from 'react-bootstrap';
import { CAccordion, CAccordionBody, CAccordionHeader, CAccordionItem } from '@coreui/react';
import axios from '../utils/AxiosInstance';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { imageUrl } from '../utils/helper';
import './MachineDetail.css';

const MachineDetail = () => {
  const { machineId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [machineData, setMachineData] = useState(null);

  useEffect(() => {
    fetchMachineConfig();
  }, [machineId]);

  const fetchMachineConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/machines/config/${machineId}`);
      if (response.data.success) {
        setMachineData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching machine config:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!machineData) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <i className="fas fa-exclamation-triangle text-warning fs-1 mb-3"></i>
          <h4>Machine not found</h4>
        </div>
      </Container>
    );
  }

  const { machine, categories } = machineData;

  return (
    <Container fluid className="machine-detail-container">
      {/* Header Section */}
      <div className="d-flex align-items-center mb-4">
        <button
          className="btn btn-link text-decoration-none p-0 me-3"
          onClick={() => navigate('/admin/machines')}
        >
          <i className="fas fa-arrow-left fs-5"></i>
        </button>
        <h3 className="mb-0 fw-bold">Machine Configuration</h3>
      </div>

      {/* Machine Info Card */}
      <Card className="machine-info-card shadow-sm mb-4">
        <Card.Body className="p-4">
          <div className="row align-items-center">
            <div className="col-md-3">
              <div className="info-item">
                <i className="fas fa-robot text-primary me-2"></i>
                <div>
                  <small className="text-muted d-block">Machine Name</small>
                  <strong>{machine.name}</strong>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="info-item">
                <i className="fas fa-id-card text-info me-2"></i>
                <div>
                  <small className="text-muted d-block">Machine ID</small>
                  <strong>{machine.machineId}</strong>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="info-item">
                <i className="fas fa-map-marker-alt text-danger me-2"></i>
                <div>
                  <small className="text-muted d-block">Location</small>
                  <strong>{machine.location}</strong>
                </div>
              </div>
            </div>
            <div className="col-md-2 text-end">
              <Badge
                bg={machine.status === 'online' ? 'success' : 'danger'}
                className="px-3 py-2 fs-6"
              >
                <i className={`fas ${machine.status === 'online' ? 'fa-wifi' : 'fa-power-off'} me-2`}></i>
                {machine.status.toUpperCase()}
              </Badge>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Categories Accordion */}
      <Card className="categories-card shadow-sm">
        <Card.Body className="p-4">
          <h5 className="mb-4 fw-bold">
            <i className="fas fa-layer-group text-primary me-2"></i>
            Product Categories ({categories.length})
          </h5>

          <CAccordion activeItemKey={1}>
            {categories.map((category, index) => (
              <CAccordionItem itemKey={index + 1} key={index}>
                <CAccordionHeader>
                  <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                    <span className="fw-semibold">
                      <i className="fas fa-box me-2 text-primary"></i>
                      {category.categoryName}
                    </span>
                    <Badge bg="primary" pill className="ms-2">
                      {category.products.length} Products
                    </Badge>
                  </div>
                </CAccordionHeader>
                <CAccordionBody>
                  <div className="table-responsive">
                    <Table hover className="products-table mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '80px' }}>Image</th>
                          <th>Product Name</th>
                          <th>Description</th>
                          <th>Slot</th>
                          <th>Quantity</th>
                          <th style={{ width: '120px' }}>Base Price</th>
                          <th style={{ width: '100px' }}>Discount</th>
                          <th style={{ width: '120px' }}>Final Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {category.products.map((product) => (
                          <tr key={product.id}>
                            <td>
                              {product.image && (
                                <img
                                  src={imageUrl(product.image)}
                                  alt={product.name}
                                  className="product-thumbnail"
                                  style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
                                />
                              )}
                            </td>
                            <td>
                              <div className="fw-semibold text-dark">{product.name}</div>
                            </td>
                            <td>
                              <div className="product-description text-muted">
                                {product.description || 'No description available'}
                              </div>
                            </td>
                            <td>
                              <span className="fw-semibold">{product?.slotNumber}</span>
                            </td>  
                                <td>
                              <span className="fw-semibold">{product?.stock}</span>
                            </td> 
                            <td>
                              <span className="fw-semibold">{product.basePrice}</span>
                            </td>
                            <td>
                              {product.isDiscounted ? (
                                <Badge bg="success" className="w-100">
                                  {product.discountValue}% OFF
                                </Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <span className="fw-bold text-success fs-6">{product.finalPrice}</span>
                                {product.isDiscounted && (
                                  <small className="text-decoration-line-through text-muted">
                                    {product.basePrice}
                                  </small>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CAccordionBody>
              </CAccordionItem>
            ))}
          </CAccordion>

          {categories.length === 0 && (
            <div className="text-center py-5">
              <i className="fas fa-box-open text-muted fs-1 mb-3"></i>
              <p className="text-muted">No categories configured for this machine</p>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default MachineDetail;
